import axios, { AxiosError } from 'axios';
import { ApplyFormErrors, IApplyFormValues, IJobItem } from 'types/recruit';

export const MAX_APPLY_FILE_SIZE = 10 * 1024 * 1024;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9][0-9\s-]{7,19}$/;

export const JOB_FILTER_FIELDS = [
  'division',
  'job',
  'career',
  'employment',
  'place',
] as const;

export type JobFilterField = typeof JOB_FILTER_FIELDS[number];

export type JobFilters = Partial<Record<JobFilterField, string>>;

export type JobFilterOptions = Record<JobFilterField, string[]>;

interface IGetJobFieldValue {
  attributes: IJobItem['attributes'];
  field: JobFilterField;
}

/**
 * 공고에서 필터 필드 값을 읽는다.
 * job 필드는 legacy occupation 값으로 폴백하며, 공백을 정리한 값이 없으면 null을 반환한다.
 */
export const getJobFieldValue = ({ attributes, field }: IGetJobFieldValue) => {
  const value =
    field === 'job'
      ? attributes.job || attributes.occupation
      : attributes[field];
  return value?.trim() || null;
};

interface IGetJobMetaItems {
  attributes: IJobItem['attributes'];
}

/**
 * 채용 공고 메타 정보(직군/경력/고용형태/근무지 등)를 배열로 변환한다.
 */
export const getJobMetaItems = ({ attributes }: IGetJobMetaItems) =>
  JOB_FILTER_FIELDS.map((field) => ({
    field,
    value: getJobFieldValue({ attributes, field }),
  })).filter((item): item is { field: JobFilterField; value: string } =>
    Boolean(item.value)
  );

interface IGetJobFilterOptions {
  jobs: IJobItem[];
}

/**
 * 공고 목록에서 필터 필드별 고유 옵션 목록을 등장 순서대로 추출한다.
 */
export const getJobFilterOptions = ({ jobs }: IGetJobFilterOptions) =>
  Object.fromEntries(
    JOB_FILTER_FIELDS.map((field) => [
      field,
      Array.from(
        new Set(
          jobs
            .map(({ attributes }) => getJobFieldValue({ attributes, field }))
            .filter((value): value is string => Boolean(value))
        )
      ),
    ])
  ) as JobFilterOptions;

interface IFilterJobs {
  jobs: IJobItem[];
  search: string;
  filters: JobFilters;
}

/**
 * 제목 검색어와 필드별 선택값(AND 조건)으로 공고 목록을 필터링한다.
 */
export const filterJobs = ({ jobs, search, filters }: IFilterJobs) => {
  const keyword = search.trim().toLowerCase();
  return jobs.filter(
    ({ attributes }) =>
      attributes.title.toLowerCase().includes(keyword) &&
      JOB_FILTER_FIELDS.every((field) => {
        const selected = filters[field];
        return (
          !selected || getJobFieldValue({ attributes, field }) === selected
        );
      })
  );
};

interface IIsValidApplyFile {
  file: File;
}

/**
 * 지원서 첨부 파일이 PDF이면서 허용 용량 이내인지 검사한다.
 */
export const isValidApplyFile = ({ file }: IIsValidApplyFile) =>
  file.type === 'application/pdf' && file.size <= MAX_APPLY_FILE_SIZE;

interface IValidateApplyForm {
  values: IApplyFormValues;
}

/**
 * 지원 폼 입력값을 검증해 필드별 에러 메시지 키를 반환한다.
 * 에러가 없으면 빈 객체를 반환한다.
 */
export const validateApplyForm = ({
  values,
}: IValidateApplyForm): ApplyFormErrors => {
  const {
    name,
    email,
    phone,
    resume,
    portfolioType,
    portfolioFile,
    portfolioUrl,
    privacyRequired,
  } = values;

  return {
    ...(name.trim() ? {} : { name: 'error-name-required' }),
    ...(email.trim()
      ? EMAIL_REGEX.test(email.trim())
        ? {}
        : { email: 'error-email-invalid' }
      : { email: 'error-email-required' }),
    ...(phone.trim()
      ? PHONE_REGEX.test(phone.trim())
        ? {}
        : { phone: 'error-phone-invalid' }
      : { phone: 'error-phone-required' }),
    ...(resume
      ? isValidApplyFile({ file: resume })
        ? {}
        : { resume: 'error-file-invalid' }
      : { resume: 'error-resume-required' }),
    ...(portfolioType === 'file' &&
    resume &&
    portfolioFile &&
    resume.size + portfolioFile.size > MAX_APPLY_FILE_SIZE
      ? { portfolioFile: 'submit-error-file-too-large' }
      : {}),
    ...(portfolioType === 'file' && portfolioFile
      ? isValidApplyFile({ file: portfolioFile })
        ? {}
        : { portfolioFile: 'error-file-invalid' }
      : {}),
    ...(portfolioType === 'url' &&
    portfolioUrl.trim() &&
    !/^https?:\/\/.+/.test(portfolioUrl.trim())
      ? { portfolioUrl: 'error-portfolio-url-invalid' }
      : {}),
    ...(privacyRequired ? {} : { privacyRequired: 'error-privacy-required' }),
  };
};

interface ISubmitJobApplication {
  jobId: number;
  values: IApplyFormValues;
}

/**
 * 지원서를 Strapi에 제출한다.
 * entry 데이터와 첨부 파일을 multipart/form-data로 한 번에 전송한다.
 */
export const submitJobApplication = ({
  jobId,
  values,
}: ISubmitJobApplication) => {
  const {
    name,
    email,
    phone,
    resume,
    portfolioType,
    portfolioFile,
    portfolioUrl,
    privacyRequired,
    privacyOptional,
  } = values;

  const formData = new FormData();
  formData.append(
    'data',
    JSON.stringify({
      job: jobId,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      portfolioUrl:
        portfolioType === 'url' && portfolioUrl.trim()
          ? portfolioUrl.trim()
          : null,
      privacyRequired,
      privacyOptional,
    })
  );
  if (resume) formData.append('files.resume', resume);
  if (portfolioType === 'file' && portfolioFile) {
    formData.append('files.portfolioFile', portfolioFile);
  }

  return axios.post('/api/job-application-jas', formData);
};

interface IGetSubmitErrorKey {
  error: unknown;
}

interface IStrapiValidationDetail {
  path?: string;
}

interface IStrapiErrorBody {
  error?: { details?: { errors?: IStrapiValidationDetail[] } };
}

const isJobUnavailableError = (error: AxiosError<IStrapiErrorBody>) => {
  const details = error.response?.data?.error?.details?.errors ?? [];
  return (
    error.response?.status === 400 && details.some(({ path }) => path === 'job')
  );
};

export const getSubmitErrorKey = ({ error }: IGetSubmitErrorKey) => {
  if (!(error instanceof AxiosError)) return 'submit-error';
  if (error.response?.status === 413) return 'submit-error-file-too-large';
  if (isJobUnavailableError(error)) return 'closed-description';
  return 'submit-error';
};
